import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Menu, X, Globe } from 'lucide-react';
import { Button } from './ui/button';

const WHATSAPP_LINK = "https://wa.me/529841585732?text=Hola%2C%20vi%20la%20p%C3%A1gina%20de%20Marea%20Riviera%20y%20quiero%20una%20asesor%C3%ADa%20sin%20costo.";

export const Navbar = () => {
  const { language, toggleLanguage, t } = useLanguage();
  const location = useLocation();
  const isHomePage = location.pathname === '/';
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleScroll = useCallback(() => {
    setIsScrolled(window.scrollY > 50);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const scrollToSection = (sectionId) => {
    if (isHomePage) {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
        setIsMobileMenuOpen(false);
      }
    } else {
      sessionStorage.setItem('scrollTo', sectionId);
      window.location.href = '/';
    }
  };

  const showDarkNav = !isHomePage || isScrolled;

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 w-full z-50 transition-all duration-500 ${
        showDarkNav
          ? 'bg-[#0A2235]/95 backdrop-blur-xl shadow-lg'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-16 lg:px-24">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <a href="/" className="flex-shrink-0 flex items-center gap-4" data-testid="navbar-logo">
            <svg className="h-12 w-auto" viewBox="0 0 140 80" xmlns="http://www.w3.org/2000/svg">
              <path d="M10 45 Q30 20 50 40 Q70 60 90 35 Q110 10 130 30" fill="none" stroke="#7ECFCF" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M10 58 Q30 33 50 53 Q70 73 90 48 Q110 23 130 43" fill="none" stroke="#2BBFBF" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <div className="leading-tight notranslate" translate="no">
              <span className="font-playfair text-2xl text-[#FAFAF7] block tracking-tight">Marea</span>
              <span className="font-playfair text-lg text-[#2BBFBF] block -mt-1">Riviera</span>
            </div>
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            <button
              onClick={() => scrollToSection('properties')}
              className="text-[#FAFAF7]/80 hover:text-[#2BBFBF] transition-colors text-sm tracking-wide font-light"
              data-testid="nav-properties"
            >
              {t('nav.properties')}
            </button>
            <button
              onClick={() => scrollToSection('calculator')}
              className="text-[#FAFAF7]/80 hover:text-[#2BBFBF] transition-colors text-sm tracking-wide font-light"
              data-testid="nav-calculator"
            >
              {t('nav.calculator')}
            </button>
            <button
              onClick={() => scrollToSection('zones')}
              className="text-[#FAFAF7]/80 hover:text-[#2BBFBF] transition-colors text-sm tracking-wide font-light"
              data-testid="nav-zones"
            >
              {t('nav.zones')}
            </button>
            <button
              onClick={() => scrollToSection('contact')}
              className="text-[#FAFAF7]/80 hover:text-[#2BBFBF] transition-colors text-sm tracking-wide font-light"
              data-testid="nav-contact"
            >
              {t('nav.contact')}
            </button>

            {/* Language Switcher */}
            <button
              onClick={toggleLanguage}
              className="flex items-center gap-1 text-[#FAFAF7]/80 hover:text-[#2BBFBF] transition-colors text-sm"
              data-testid="language-switcher"
            >
              <Globe className="w-4 h-4" />
              <span className="uppercase font-medium">{language}</span>
            </button>

            {/* CTA Button */}
            <Button
              asChild
              className="bg-[#2BBFBF] hover:bg-[#7ECFCF] text-[#0A2235] font-medium rounded-full px-6 transition-all duration-300 active:scale-95"
              data-testid="nav-cta"
            >
              <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer">
                {t('nav.freeConsult')}
              </a>
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center gap-4">
            <button
              onClick={toggleLanguage}
              className="flex items-center gap-1 text-[#FAFAF7]/80 hover:text-[#2BBFBF] transition-colors text-sm"
              data-testid="mobile-language-switcher"
            >
              <Globe className="w-4 h-4" />
              <span className="uppercase font-medium">{language}</span>
            </button>
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="text-[#FAFAF7]"
              data-testid="mobile-menu-toggle"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-[#0A2235] border-t border-[#FAFAF7]/10 py-6 space-y-4" data-testid="mobile-menu">
            <button
              onClick={() => scrollToSection('properties')}
              className="block w-full text-left text-[#FAFAF7]/80 hover:text-[#2BBFBF] transition-colors py-2"
            >
              {t('nav.properties')}
            </button>
            <button
              onClick={() => scrollToSection('calculator')}
              className="block w-full text-left text-[#FAFAF7]/80 hover:text-[#2BBFBF] transition-colors py-2"
            >
              {t('nav.calculator')}
            </button>
            <button
              onClick={() => scrollToSection('zones')}
              className="block w-full text-left text-[#FAFAF7]/80 hover:text-[#2BBFBF] transition-colors py-2"
            >
              {t('nav.zones')}
            </button>
            <button
              onClick={() => scrollToSection('contact')}
              className="block w-full text-left text-[#FAFAF7]/80 hover:text-[#2BBFBF] transition-colors py-2"
            >
              {t('nav.contact')}
            </button>
            <a
              href={WHATSAPP_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full bg-[#2BBFBF] text-[#0A2235] text-center py-3 font-medium mt-4 rounded-full"
            >
              {t('nav.freeConsult')}
            </a>
          </div>
        )}
      </div>
    </nav>
  );
};
